/**
 * Table — Semantic HTML table for structured, columnar data.
 *
 * Table Component Guidelines
 * - Use Table for data that naturally fits rows and columns (e.g. user lists,
 *   billing history, analytics). For non-tabular content that just looks like rows
 *   (e.g. settings, navigation), use ListItem-based layouts instead.
 * - Compose with `TableHeader`, `TableBody`, `TableRow`, `TableHead`, `TableCell`,
 *   and optionally `TableFooter` and `TableCaption`.
 * - Table automatically handles horizontal overflow with a scrollable wrapper — no
 *   extra overflow handling needed.
 * - Use `TableCaption` for accessible table descriptions when the table's purpose
 *   isn't obvious from surrounding context.
 * @see https://ui.shadcn.com/docs/components/table shadcn/ui Table documentation
 */
"use client";

import type { LayoutClassName } from "@replo/design-system/types/layoutClassName";

import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";

interface TableProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"table">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function Table<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableProps<ClassName>) {
  return (
    <div
      data-slot="table-container"
      className="relative w-full overflow-x-auto"
    >
      <table
        data-slot="table"
        className={twMerge(
          "w-full caption-bottom text-sm",
          className,
          UNSAFE_className,
        )}
        {...props}
      />
    </div>
  );
}

interface TableHeaderProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"thead">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TableHeader<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableHeaderProps<ClassName>) {
  return (
    <thead
      data-slot="table-header"
      className={twMerge(
        "[&_tr]:border-b [&_tr]:border-border",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface TableBodyProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"tbody">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TableBody<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableBodyProps<ClassName>) {
  return (
    <tbody
      data-slot="table-body"
      className={twMerge(
        "[&_tr:last-child]:border-0",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface TableFooterProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"tfoot">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TableFooter<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableFooterProps<ClassName>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={twMerge(
        "bg-sheet border-t border-border font-medium [&>tr]:last:border-b-0",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface TableRowProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"tr">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TableRow<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableRowProps<ClassName>) {
  return (
    <tr
      data-slot="table-row"
      className={twMerge(
        "hover:bg-surface data-[state=selected]:bg-surface border-b border-border transition-colors",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface TableHeadProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"th">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TableHead<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableHeadProps<ClassName>) {
  return (
    <th
      data-slot="table-head"
      className={twMerge(
        "text-muted h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface TableCellProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"td">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TableCell<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableCellProps<ClassName>) {
  return (
    <td
      data-slot="table-cell"
      className={twMerge(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

interface TableCaptionProps<ClassName extends string = string>
  extends Omit<React.ComponentProps<"caption">, "className"> {
  className?: LayoutClassName<ClassName>;
  UNSAFE_className?: string;
}

function TableCaption<ClassName extends string>({
  className,
  UNSAFE_className,
  ...props
}: TableCaptionProps<ClassName>) {
  return (
    <caption
      data-slot="table-caption"
      className={twMerge(
        "text-muted pt-4 text-center text-sm",
        className,
        UNSAFE_className,
      )}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
